import axios from "axios";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: false,
});

let authTokenPromise: Promise<void> | null = null;
let authTokenResolver: (() => void) | null = null;

export const setAuthToken = (token: string) => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  if (authTokenResolver) {
    authTokenResolver();
    authTokenResolver = null;
  }
};

export const removeAuthToken = () => {
  delete api.defaults.headers.common['Authorization'];
  authTokenPromise = null;
  authTokenResolver = null;
};

export const waitForAuthToken = () => {
  if (!authTokenPromise) {
    authTokenPromise = new Promise((resolve) => {
      authTokenResolver = resolve;
    });
  }
  return authTokenPromise;
};

/**
 * Dynamic token provider (used when embedded in a host like UnitLogic).
 * The host supplies a `() => Promise<string>` that returns a fresh access
 * token; we attach it per-request so long sessions keep working across token
 * refreshes. Registered once; swapping the provider just replaces the fn.
 */
let tokenProvider: (() => Promise<string | null | undefined>) | null = null;
let tokenProviderInterceptorId: number | null = null;

export const setTokenProvider = (
  provider: (() => Promise<string | null | undefined>) | null
) => {
  tokenProvider = provider;
  if (provider && tokenProviderInterceptorId === null) {
    tokenProviderInterceptorId = api.interceptors.request.use(async (config) => {
      if (tokenProvider) {
        try {
          const token = await tokenProvider();
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (err) {
          console.error("[RoboticsBundle] tokenProvider failed:", err);
        }
      }
      return config;
    });
  }
};

let requestInterceptorId: number | null = null;

export const setupAxiosInterceptor = (logout: () => void) => {
  if (requestInterceptorId !== null) return;

  requestInterceptorId = api.interceptors.request.use(
    async (config) => {
      if (authTokenPromise) {
        await authTokenPromise;
      }

      const token = api.defaults.headers.common['Authorization'];
      if (token && config.headers) {
        config.headers.Authorization = token;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        removeAuthToken();
        logout();
      }
      return Promise.reject(error);
    }
  );
};